import { getJiraAttachmentId } from 'sidecar-utilities'

// Jira stores images as media nodes, which can be previewed as thumbnails.
const MEDIA_NODE_TYPES = ['mediaSingle', 'mediaInline', 'mediaGroup']

const PDF_FILENAME = /\.pdf$/i
const IMAGE_FILENAME = /\.(jpe?g|png|gif|heic|heif|webp|tiff?|bmp|svg)$/i

/**
 * Walk an Atlassian Document Format body and collect its attachments.
 *
 * Images are stored as media nodes and previewed via the thumbnail endpoint.
 * Every other file type — PDFs, HEIC photos from an iPhone, Office docs — is
 * stored as a text node linking to a Jira REST URL that only Jira can open, so
 * we pull the filename and attachment id back out of it and offer our own
 * download instead.
 *
 * @param {object} document - The comment body in ADF format
 * @returns {{
 *   hasMedia: boolean
 *   fileAttachments: { attachmentId: string; filename: string }[]
 * }}
 *   Whether the body has media nodes worth fetching thumbnails for, plus the
 *   linked file attachments
 */
export const collectAttachments = (document = {}) => {
  let hasMedia = false
  // Keyed by attachment id so the same file linked twice is only listed once
  const fileAttachments = new Map()

  const walk = (nodes = []) => {
    nodes.forEach(node => {
      if (MEDIA_NODE_TYPES.includes(node.type)) {
        hasMedia = true
        return
      }

      const attachmentId = getLinkedAttachmentId(node)
      if (attachmentId) {
        if (!fileAttachments.has(attachmentId)) {
          fileAttachments.set(attachmentId, {
            attachmentId,
            filename: node.text?.trim() || 'Attachment',
          })
        }
        return
      }

      if (Array.isArray(node.content)) walk(node.content)
    })
  }
  walk(document?.content)

  return { hasMedia, fileAttachments: [...fileAttachments.values()] }
}

/**
 * Get the Jira attachment id a text node links to, if any
 *
 * @param {object} node - An ADF node
 * @returns {string | null} The attachment id, or null if the node isn't a link
 *   to an attachment
 */
export const getLinkedAttachmentId = node => {
  if (node?.type !== 'text') return null
  const linkMark = node.marks?.find(mark => mark.type === 'link')
  return getJiraAttachmentId(linkMark?.attrs?.href)
}

/**
 * Pick the icon that best represents a file we can't preview
 *
 * @param {string} filename - The attachment filename
 * @returns {string} An Icon name
 */
export const getAttachmentIconName = (filename = '') => {
  if (PDF_FILENAME.test(filename)) return 'pdf'
  if (IMAGE_FILENAME.test(filename)) return 'image'
  return 'document'
}

/**
 * Whether a fetched thumbnail can actually be shown as an image. Jira only
 * generates thumbnails for image types it understands, so entries for anything
 * else come back without usable image data.
 *
 * @param {object} thumbnail - A thumbnail entry from the attachment endpoint
 * @returns {boolean} Whether the thumbnail has displayable image content
 */
export const isDisplayableThumbnail = ({ contentType = '', content } = {}) =>
  !!content && contentType.startsWith('image/')

/**
 * Generate the author name for the specifc message bubble
 *
 * @param {string} displayName - The display name of the author based on the
 *   Jira comment
 * @param {string} memberName - The logged-in user's first name or the member
 *   name of the ticket
 * @returns {object} - The author object of the message bubble
 */
export const generateAuthorName = (comment, memberName, members) => {
  // first we want to see if the account has access to the Member object
  // if so, and if the comment has an associated memberUuid, we want to display the member's name based on the memberUuid
  // otherwise, we default to using the displayName value to determine the author's name
  // if the displayname is the same as the member's name, we want to display the member's First Name
  // and show a different color message bubble

  // Define the author object for the ChatBubble component
  const author = { firstName: '', lastName: '', image: '' }
  //TODO Remove the conditional once BE pushes their changes
  const displayName =
    comment.author?.displayName || comment.updateAuthor?.displayName
  const memberUuid = comment.memberUuid

  //Big if-else chain to try to cover as many scenarios as possible

  //Case 1: The member object contains the memberUuid of the poster, so we can use that to get the member's name
  if (members && Object.keys(members).includes(memberUuid)) {
    const newMember = { ...members[memberUuid] }
    newMember.lastName = ''
    return newMember
    //Case 2: the memberUuid exists, but it's not present in the members object. This means that the current member is not the primary account holder
    //as that individual would have all uuids on the account in the members object. Therefore, since inbox messages can only be passed from the primary
    //account holder to a dependant, we can assume that in this scenario, the current message is from the Primary Member/Account Holder.
  } else if (memberUuid) {
    author.firstName = 'Primary Member'
    //Case 3: Now we're back to the non-uuid based logic. If the UUID is missing entirely (possible in a few scenarios due to bugs unrelated to these changes)
    //and the message was definitely sent from the member app, it's safest to default to assuming the message was sent by the current member,
    //since that's by far the most common scenario.
  } else if (displayName === 'Member App') {
    author.firstName = memberName
    //Case 4: If the message display name is 'Automation for Jira', that means the message was generated by an internal staff member in Jira.
  } else if (displayName === 'Automation for Jira') {
    author.firstName = 'Member Care'
  } else {
    //Case 5: Backup case, if all else fails we'll just assume the message is from the current member.
    // We only want to display the first name of the author if it's not the member
    author.firstName = displayName.split(' ')[0]
  }

  return author
}
